import * as d3 from 'd3';
import { useState } from 'react';
import { mergeProps, useHover, usePress } from 'react-aria';
import { animated, useSpring } from 'react-spring';

import {
  CenterTooltipContent,
  EntryTooltipContent,
  OverflowTooltipContent,
} from './DonutChartTooltipContent';
import { DonutChartEntry, InternalEntry, InternalOverflowEntry } from './types';
import { processEntries } from './utils';

import SVGWithDimensions from '~/components/Charts/ChartsD3/SVGWithDimensions';
import { useChartDimensions } from '~/components/Charts/ChartsD3/useChartDimensions';
import { Tooltip } from '~/components/scales/Tooltip';

type Props = {
  /**
   * The diameter prop is required and determines the size of the donut chart.
   * In the future, we might consider making this optional to allow the
   * component to be responsive.
   *
   * There are three size classes related to the diameter that affect the font
   * size of the label and sublabel:
   * - Large: 230 <= diameter <= 370
   * - Medium: 100 <= diameter < 230
   * - Small: 56 <= diameter < 100
   */
  diameter?: number;
  entries: DonutChartEntry[];
  formatValue?: (value: number) => string;
  hasOverflow?: boolean;
  isSorted?: boolean;
  label?: string;
  sublabel?: string;
};

/**
 * DonutChart renders a donut chart using D3 and SVG. It is designed to minimize
 * the configuration required by the caller, relying primarily on the
 * DonutChartEntry type for its display. The component supports more minor
 * customization through props such as label, sublabel, isSorted, formatValue,
 * and hasOverflow.
 */
export default function DonutChart(props: Props) {
  if (props.diameter && props.diameter < 56) {
    throw new Error('The diameter of the DonutChart must be at least 56px');
  } else if (props.diameter && props.diameter > 370) {
    throw new Error('The diameter of the DonutChart must be at most 370px');
  }

  const margins = { marginBottom: 0, marginLeft: 0, marginRight: 0, marginTop: 0 };
  const { ref, dimensions } = useChartDimensions(
    props.diameter ? { ...margins, height: props.diameter, width: props.diameter } : margins
  );

  let diameter = Math.min(dimensions.boundedWidth, dimensions.boundedHeight) ?? 0;
  if (diameter < 56) diameter = 56;
  if (diameter > 370) diameter = 370;

  const { entries, formatPercent } = processEntries(
    props.entries,
    props.isSorted,
    props.hasOverflow
  );

  return (
    <SVGWithDimensions
      ref={ref}
      className={props.diameter ? 'h-min w-min' : undefined}
      dimensions={dimensions}
    >
      <g transform={`translate(${dimensions.boundedWidth / 2}, ${dimensions.boundedHeight / 2})`}>
        <Center
          diameter={diameter}
          entries={entries}
          formatPercent={formatPercent}
          formatValue={props.formatValue}
          label={props.label}
          sublabel={props.sublabel}
        />
        <Ring
          diameter={diameter}
          entries={entries}
          formatPercent={formatPercent}
          formatValue={props.formatValue}
        />
      </g>
    </SVGWithDimensions>
  );
}

/**
 * Renders the center of the donut chart with a label, sublabel, and a tooltip
 * listing all entries. Displays "None" if no entries are provided.
 */
function Center(props: {
  diameter: number;
  entries: (InternalEntry | InternalOverflowEntry)[];
  formatPercent: (value: number) => string;
  formatValue?: (value: number) => string;
  label?: string;
  sublabel?: string;
}) {
  const arcPathGenerator = d3.arc();
  const innerRadius = 0;
  const outerRadius = props.diameter / 3;
  const startAngle = 0;
  const endAngle = 2 * Math.PI;

  const isEmpty = !props.entries.some(({ value }) => value > 0);
  const isLarge = (diameter: number) => diameter >= 230;
  const isMedium = (diameter: number) => diameter < 230 && diameter >= 100;
  const isSmall = (diameter: number) => diameter < 100;

  const label = isEmpty ? 'None' : props.label;
  const sublabel = isEmpty || isSmall(props.diameter) ? undefined : props.sublabel;
  const labelClass = [
    isEmpty ? 'fill-type-muted' : '',
    isLarge(props.diameter) ? 'type-body1 !font-bold' : '',
    isMedium(props.diameter) ? 'type-body2 !font-bold' : '',
    isSmall(props.diameter) ? 'type-body3' : '',
  ].join(' ');
  const sublabelClass = [
    isLarge(props.diameter) ? 'type-body2' : '',
    isMedium(props.diameter) ? 'type-body3' : '',
  ].join(' ');

  return (
    <Tooltip
      content={
        <CenterTooltipContent
          entries={props.entries}
          formatPercent={props.formatPercent}
          formatValue={props.formatValue}
        />
      }
      isDisabled={isEmpty}
      // isInteractive needed for center tooltip with scrollable content
      isInteractive
      placement="bottom"
    >
      <g data-cy="donut-center">
        <path
          d={arcPathGenerator({ innerRadius, outerRadius, startAngle, endAngle }) ?? undefined}
          opacity={0}
        />
        {label && (
          <text
            className={labelClass}
            dominantBaseline="middle"
            // TODO: maxWidth does not work on svg text elements
            style={{ maxWidth: outerRadius * 2 }}
            textAnchor="middle"
            x={0}
            y={sublabel ? -10 : 0}
          >
            {label}
          </text>
        )}
        {sublabel && (
          <text
            className={sublabelClass}
            dominantBaseline="middle"
            // TODO: maxWidth does not work on svg text elements
            style={{ maxWidth: outerRadius * 2 }}
            textAnchor="middle"
            x={0}
            y={label ? 10 : 0}
          >
            {sublabel}
          </text>
        )}
      </g>
    </Tooltip>
  );
}

/**
 * Renders the donut chart ring with individual slices for each entry, including
 * a possible overflow entry. Displays an empty ring if no entries are provided.
 */
function Ring(props: {
  diameter: number;
  entries: (InternalEntry | InternalOverflowEntry)[];
  formatPercent: (value: number) => string;
  formatValue?: (value: number) => string;
}) {
  const isEmpty = !props.entries.some(({ value }) => value > 0);
  const [highlightedLabel, setHighlightedLabel] = useState<string>();

  // D3 implementation was modeled after https://www.react-graph-gallery.com/donut
  const pieGenerator = d3
    .pie<InternalEntry | InternalOverflowEntry>()
    .value((d) => d.value)
    .sort(null);
  const pie = pieGenerator(props.entries);

  const innerRadius = props.diameter / 3;
  const outerRadius = props.diameter / 2;
  const arcPathGenerator = d3.arc();

  if (isEmpty)
    return (
      <path
        className="fill-background-2"
        d={
          arcPathGenerator({ innerRadius, outerRadius, startAngle: 0, endAngle: 2 * Math.PI }) ??
          undefined
        }
        data-cy="donut-empty-slice"
      />
    );

  return (
    <>
      {pie.map((p) => (
        <Slice
          key={p.data.label}
          endAngle={p.endAngle}
          entry={p.data}
          formatPercent={props.formatPercent}
          formatValue={props.formatValue}
          highlightedLabel={highlightedLabel}
          innerRadius={innerRadius}
          onHighlight={setHighlightedLabel}
          outerRadius={outerRadius}
          startAngle={p.startAngle}
        />
      ))}
    </>
  );
}

/**
 * Renders an individual slice of the donut chart, including the tooltip and
 * interaction handlers.
 */
const Slice = (props: {
  endAngle: number;
  entry: InternalEntry | InternalOverflowEntry;
  formatPercent: (value: number) => string;
  formatValue?: (value: number) => string;
  highlightedLabel: string | undefined;
  innerRadius: number;
  onHighlight: (label: string | undefined) => void;
  outerRadius: number;
  startAngle: number;
}) => {
  const entry = props.entry;
  const hasOnClick = 'onClick' in entry && entry.onClick !== undefined;
  const isOverflowEntry = 'entries' in entry;
  const shouldReduceOpacity =
    props.highlightedLabel !== undefined && entry.label !== props.highlightedLabel;

  const { pressProps } = usePress({ onPress: hasOnClick ? entry.onClick : undefined });
  const { hoverProps } = useHover({
    onHoverStart: () => props.onHighlight(entry.label),
    onHoverEnd: () => props.onHighlight(undefined),
  });

  const arcPathGenerator = d3.arc();

  const springProps = useSpring({
    to: {
      pos: [props.startAngle, props.endAngle],
      opacity: shouldReduceOpacity ? 0.6 : 1,
    },
  });

  return (
    <Tooltip
      content={
        isOverflowEntry ? (
          <OverflowTooltipContent
            entry={entry}
            formatPercent={props.formatPercent}
            formatValue={props.formatValue}
          />
        ) : (
          <EntryTooltipContent
            entry={entry}
            formatPercent={props.formatPercent}
            formatValue={props.formatValue}
          />
        )
      }
      // isInteractive needed for overflow entry tooltip with scrollable content
      isInteractive={isOverflowEntry}
      placement="bottom"
    >
      <g {...mergeProps(hoverProps, pressProps)}>
        <animated.path
          className={hasOnClick ? 'cursor-pointer' : ''}
          d={springProps.pos.to(
            (startAngle, endAngle) =>
              arcPathGenerator({
                innerRadius: props.innerRadius,
                outerRadius: props.outerRadius,
                startAngle,
                endAngle,
              }) ?? ''
          )}
          data-cy={isOverflowEntry ? 'donut-overflow-slice' : 'donut-slice'}
          data-label={entry.label}
          fill={entry.color}
          opacity={springProps.opacity.to((opacity) => opacity)}
        />
      </g>
    </Tooltip>
  );
};
